import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import TOTPEnrollmentModal from '../../../components/auth/TOTPEnrollmentModal';
import authService from '../../../utils/authService';
import { useAuth } from '../../../contexts/AuthContext';

const SecuritySettings = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // TOTP State
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpFactorId, setTotpFactorId] = useState(null);
  const [showTOTPModal, setShowTOTPModal] = useState(false);
  const [isLoadingTOTP, setIsLoadingTOTP] = useState(true);
  const [isDisablingTOTP, setIsDisablingTOTP] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  // Session state
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  // Security actions state
  const [isLoggingOutAll, setIsLoggingOutAll] = useState(false);

  // Check TOTP status, OAuth status, and load session info on mount
  useEffect(() => {
    checkTOTPStatus();
    checkOAuthStatus();
    loadSessionInfo();
  }, [user]);

  const checkOAuthStatus = () => {
    if (user) {
      const isOAuth = authService.isOAuthUser(user);
      setIsOAuthUser(isOAuth);
    }
  };

  const checkTOTPStatus = async () => {
    setIsLoadingTOTP(true);
    try {
      const result = await authService.hasTOTPEnabled();
      if (result.success) {
        setTotpEnabled(result.enabled);
        if (result.enabled && result.factors && result.factors.length > 0) {
          setTotpFactorId(result.factors[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to check TOTP status:', error);
    } finally {
      setIsLoadingTOTP(false);
    }
  };

  const loadSessionInfo = async () => {
    setIsLoadingSession(true);
    try {
      const result = await authService.getCurrentSession();
      if (result.success) {
        setSessionInfo(result.data);
      }
    } catch (error) {
      console.error('Failed to load session info:', error);
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleEnableTOTP = () => {
    setShowTOTPModal(true);
  };

  const handleDisableTOTP = async () => {
    if (!totpFactorId) return;

    if (!confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    setIsDisablingTOTP(true);
    try {
      // First, check if we need to elevate to AAL2
      const aal = await authService.getAuthenticatorAssuranceLevel();

      if (aal.success && aal.data.needsMFA) {
        // Need to verify TOTP first
        const code = prompt('Please enter your current 6-digit authentication code to disable 2FA:');
        if (!code) {
          setIsDisablingTOTP(false);
          return;
        }

        // Verify the code to elevate to AAL2
        const verifyResult = await authService.challengeAndVerifyTOTP(totpFactorId, code);
        if (!verifyResult.success) {
          alert('Invalid code. Please try again.');
          setIsDisablingTOTP(false);
          return;
        }
      }

      // Now we're at AAL2, can unenroll
      const result = await authService.unenrollTOTP(totpFactorId);
      if (result.success) {
        setTotpEnabled(false);
        setTotpFactorId(null);
        alert('Two-factor authentication has been disabled.');
      } else {
        alert('Failed to disable 2FA: ' + result.error);
      }
    } catch (error) {
      console.error('Failed to disable TOTP:', error);
      alert('An unexpected error occurred');
    } finally {
      setIsDisablingTOTP(false);
    }
  };

  const handleTOTPSuccess = () => {
    checkTOTPStatus();
  };

  // Password change handlers
  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    try {
      const result = await authService.updatePassword(passwordForm.newPassword);

      if (result.success) {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
        alert('Password changed successfully!');
      } else {
        setPasswordErrors({ newPassword: result.error });
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordErrors({ newPassword: 'An unexpected error occurred' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    if (!confirm('This will sign you out from all devices, including this one. You\'ll need to sign in again. Continue?')) {
      return;
    }

    setIsLoggingOutAll(true);
    try {
      const result = await authService.signOutAllSessions();

      if (result.success) {
        // Redirect to login page
        navigate('/user-login');
      } else {
        alert('Failed to logout from all devices: ' + result.error);
      }
    } catch (error) {
      console.error('Failed to logout all devices:', error);
      alert('An unexpected error occurred');
    } finally {
      setIsLoggingOutAll(false);
    }
  };

  const handleDownloadSecurityReport = () => {
    // Generate security report data
    const report = {
      generatedAt: new Date().toISOString(),
      user: {
        id: user?.id,
        email: user?.email,
        createdAt: user?.created_at,
        lastSignInAt: user?.last_sign_in_at,
      },
      security: {
        twoFactorEnabled: totpEnabled,
        authMethod: isOAuthUser ? 'OAuth' : 'Email/Password',
        provider: user?.app_metadata?.provider
      },
      session: sessionInfo
    };

    // Create and download JSON file
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProviderIcon = () => {
    const provider = user?.app_metadata?.provider;
    switch (provider) {
      case 'google': return 'Chrome';
      case 'discord': return 'MessageCircle';
      case 'twitch': return 'Twitch';
      default: return 'Mail';
    }
  };

  const getProviderName = () => {
    const provider = user?.app_metadata?.provider;
    switch (provider) {
      case 'google': return 'Google';
      case 'discord': return 'Discord';
      case 'twitch': return 'Twitch';
      default: return 'Email/Password';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Security Settings</h2>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Account Overview */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Account Overview</h3>
          <div className="bg-muted/20 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Email</span>
              <span className="text-sm font-medium text-foreground">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Sign-in Method</span>
              <div className="flex items-center space-x-2">
                <Icon name={getProviderIcon()} size={16} className="text-foreground" />
                <span className="text-sm font-medium text-foreground">{getProviderName()}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Account Created</span>
              <span className="text-sm font-medium text-foreground">{formatDateTime(user?.created_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last Sign In</span>
              <span className="text-sm font-medium text-foreground">{formatDateTime(user?.last_sign_in_at)}</span>
            </div>
          </div>
        </div>

        {/* Password Management - Only for email/password users */}
        {!isOAuthUser && (
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Password</h3>
            <div className="bg-muted/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-foreground">Change Password</h4>
                  <p className="text-sm text-muted-foreground">
                    Update your password to keep your account secure
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                >
                  {showPasswordForm ? 'Cancel' : 'Change Password'}
                </Button>
              </div>

              {showPasswordForm && (
                <form onSubmit={handleSubmitPasswordChange} className="mt-6 space-y-4">
                  <Input
                    label="New Password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    error={passwordErrors.newPassword}
                    description="Must be at least 8 characters long"
                    required
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    error={passwordErrors.confirmPassword}
                    required
                  />

                  <div className="flex items-center space-x-2 pt-2">
                    <Button
                      type="submit"
                      variant="default"
                      size="sm"
                      loading={isChangingPassword}
                    >
                      Update Password
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordErrors({});
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Two-Factor Authentication */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Two-Factor Authentication</h3>
          <div className="bg-muted/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-foreground">Authenticator App (TOTP)</h4>
                  {isOAuthUser && (
                    <div className="group relative">
                      <Icon name="Info" size={16} className="text-muted-foreground cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-popover border border-border rounded-lg shadow-lg z-10">
                        <p className="text-xs text-muted-foreground">
                          You signed in using {getProviderName()}.
                          Two-factor authentication is managed by your OAuth provider.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {isOAuthUser
                    ? 'Managed by your sign-in provider'
                    : totpEnabled
                      ? 'Two-factor authentication is enabled and protecting your account'
                      : 'Add an extra layer of security to your account'
                  }
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {isLoadingTOTP ? (
                  <div className="w-6 h-6 border-2 border-muted border-t-primary rounded-full animate-spin" />
                ) : (
                  <>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      totpEnabled
                        ? 'bg-accent/10 text-accent'
                        : isOAuthUser
                          ? 'bg-muted/50 text-muted-foreground'
                          : 'bg-warning/10 text-warning'
                    }`}>
                      {totpEnabled ? 'Enabled' : 'Disabled'}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={totpEnabled ? handleDisableTOTP : handleEnableTOTP}
                      disabled={isOAuthUser || isDisablingTOTP}
                      loading={isDisablingTOTP}
                    >
                      {totpEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TOTP Enrollment Modal */}
        <TOTPEnrollmentModal
          isOpen={showTOTPModal}
          onClose={() => setShowTOTPModal(false)}
          onSuccess={handleTOTPSuccess}
        />

        {/* Current Session */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Active Session</h3>
          {isLoadingSession ? (
            <div className="bg-muted/20 rounded-lg p-4 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-muted border-t-primary rounded-full animate-spin" />
            </div>
          ) : sessionInfo ? (
            <div className="bg-muted/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Monitor" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-foreground">Current Device</h4>
                      <div className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                        Active Now
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Session expires: {formatDateTime(sessionInfo.expiresAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-muted/20 rounded-lg p-4 text-center text-sm text-muted-foreground">
              Unable to load session information
            </div>
          )}
        </div>

        {/* Security Actions */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Security Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              iconName="LogOut"
              iconPosition="left"
              onClick={handleLogoutAllDevices}
              loading={isLoggingOutAll}
              disabled={isLoggingOutAll}
            >
              Sign Out Everywhere
            </Button>

            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleDownloadSecurityReport}
            >
              Download Security Report
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Sign out everywhere will end all active sessions and require you to sign in again on all devices.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
